import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
  BrandAssetBackground,
  BrandAssetType,
  BrandTokenPatchDto,
  BrandTokenConfirmationDto,
  BrandTokenFamilyKind,
  BrandTokenRolesPatchDto,
  BrandTypeFamilyPatchDto,
  BrandTypeStylePatchDto,
  KnowledgeBaseSummaryViewDto,
  KnowledgeBaseEntryViewDto,
  KnowledgeBaseEntryUpsertDto,
  KnowledgeBaseAiPopulateDto,
  KnowledgeBaseCategoryConfigDto,
  KnowledgeBaseFileViewDto,
  DesignSystemImportLogoChoice,
  DesignSystemImportPreviewDto,
  DesignSystemImportRoleChoice,
} from '@/types/knowledgeBase'

/** URL-encode the category description for use in route parameters.
 *  The server uses TryFromDescription() which matches the Description attribute
 *  (e.g., "Ideal Customer Profile"), not the enum name. */
export function categoryToUrlParam(category: string): string {
  return encodeURIComponent(category)
}

// Colors took the plural before the other families had routes of their own.
const BRAND_TOKEN_ROUTES: Record<BrandTokenFamilyKind, string> = {
  Color: 'Colors',
  Spacing: 'Spacing',
  Radius: 'Radius',
  Shadow: 'Shadow',
}

/** Whose Brand Guidelines a write goes to: the company's, or one workspace's own (ORB-1027). */
export interface KnowledgeBaseOwner {
  clientCompanyId: string
  workspaceId?: string | null
}

function ownerBase({ clientCompanyId, workspaceId }: KnowledgeBaseOwner): string {
  return workspaceId
    ? `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase`
    : `/api/ClientCompanies/${clientCompanyId}/KnowledgeBase`
}

function fileQuery(assetType?: BrandAssetType, background?: BrandAssetBackground | null): string {
  const params = new URLSearchParams()
  if (assetType) params.set('assetType', assetType)
  if (background) params.set('background', background)
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const knowledgeBaseApi = {
  // ---------- METADATA ----------

  getCategories: (): Promise<ApiResponse<KnowledgeBaseCategoryConfigDto[]>> =>
    apiClient.get('/api/KnowledgeBase/Categories'),

  // ---------- COMPANY LEVEL ----------

  getCompanySummary: (
    clientCompanyId: string
  ): Promise<ApiResponse<KnowledgeBaseSummaryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase`),

  getCompanyEntry: (
    clientCompanyId: string,
    category: string
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  upsertCompanyEntry: (
    clientCompanyId: string,
    category: string,
    dto: KnowledgeBaseEntryUpsertDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}`, dto),

  uploadCompanyFile: (
    clientCompanyId: string,
    category: string,
    file: File,
    assetType?: BrandAssetType,
    background?: BrandAssetBackground | null
  ): Promise<ApiResponse<KnowledgeBaseFileViewDto>> => {
    const formData = new FormData()
    formData.append('file', file)
    const query = fileQuery(assetType, background)
    return apiClient.post(
      `/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}/Files${query}`,
      formData
    )
  },

  deleteCompanyFile: (
    clientCompanyId: string,
    category: string,
    fileId: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/KnowledgeBase/${categoryToUrlParam(category)}/Files/${fileId}`),

  // ---------- BRAND GUIDELINES (company-wide or one workspace's own) ----------

  /** Brand Guidelines' roles: null leaves one as it is and '' removes it. A refusal is keyed by the role's name. */
  patchBrandTokenRoles: (
    owner: KnowledgeBaseOwner,
    dto: BrandTokenRolesPatchDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.patch(`${ownerBase(owner)}/BrandTokens/Roles`, dto),

  /** One of Brand Guidelines' own colors, spacing steps, radii or shadows: added when `Current` is absent, removed with `Remove`. A refusal is keyed by the field's name. */
  patchBrandToken: (
    owner: KnowledgeBaseOwner,
    family: BrandTokenFamilyKind,
    dto: BrandTokenPatchDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.patch(`${ownerBase(owner)}/BrandTokens/${BRAND_TOKEN_ROUTES[family]}`, dto),

  /** One of Brand Guidelines' own type styles: a new one joins `Group`, and a new group needs the `Family` it is set in. */
  patchBrandTypeStyle: (
    owner: KnowledgeBaseOwner,
    dto: BrandTypeStylePatchDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.patch(`${ownerBase(owner)}/BrandTokens/Type/Styles`, dto),

  /** One of Brand Guidelines' font families, by key. A family a group is set in cannot be removed or renamed. */
  patchBrandTypeFamily: (
    owner: KnowledgeBaseOwner,
    dto: BrandTypeFamilyPatchDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.patch(`${ownerBase(owner)}/BrandTokens/Type/Families`, dto),

  /** Brand Guidelines' token set, as reviewed. `ProvenanceAt` is the set's `Provenance.At` as it was shown, sent back unparsed. */
  confirmBrandTokens: (
    owner: KnowledgeBaseOwner,
    dto: BrandTokenConfirmationDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.post(`${ownerBase(owner)}/BrandTokens/Confirmation`, dto),

  /** Marks a brand file for light or dark backgrounds; null makes it work on either (ORB-968). */
  patchFileBackground: (
    owner: KnowledgeBaseOwner,
    category: string,
    fileId: string,
    background: BrandAssetBackground | null
  ): Promise<ApiResponse<KnowledgeBaseFileViewDto>> =>
    apiClient.patch(`${ownerBase(owner)}/${categoryToUrlParam(category)}/Files/${fileId}`, { Background: background }),

  /** Reads a Claude design-system zip and says what importing it would change. Nothing is saved (ORB-1028). */
  previewDesignSystemImport: (
    owner: KnowledgeBaseOwner,
    zip: File
  ): Promise<ApiResponse<DesignSystemImportPreviewDto>> => {
    const formData = new FormData()
    formData.append('file', zip)
    return apiClient.post(`${ownerBase(owner)}/BrandTokens/Import/Preview`, formData)
  },

  /**
   * Imports the zip again with the person's choice for each logo found in it, and the token they chose for each role the
   * design system's own names do not settle, replacing the brand at that level.
   */
  importDesignSystem: (
    owner: KnowledgeBaseOwner,
    zip: File,
    logos: DesignSystemImportLogoChoice[],
    roles: DesignSystemImportRoleChoice[]
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> => {
    const formData = new FormData()
    formData.append('file', zip)
    formData.append('Choices', JSON.stringify({ Logos: logos, Roles: roles }))
    return apiClient.post(`${ownerBase(owner)}/BrandTokens/Import`, formData)
  },

  // ---------- WORKSPACE LEVEL ----------

  getWorkspaceSummary: (
    clientCompanyId: string,
    workspaceId: string
  ): Promise<ApiResponse<KnowledgeBaseSummaryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase`),

  getWorkspaceEntry: (
    clientCompanyId: string,
    workspaceId: string,
    category: string
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  upsertWorkspaceEntry: (
    clientCompanyId: string,
    workspaceId: string,
    category: string,
    dto: KnowledgeBaseEntryUpsertDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}`, dto),

  deleteWorkspaceOverride: (
    clientCompanyId: string,
    workspaceId: string,
    category: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  uploadWorkspaceFile: (
    clientCompanyId: string,
    workspaceId: string,
    category: string,
    file: File,
    assetType?: BrandAssetType,
    background?: BrandAssetBackground | null
  ): Promise<ApiResponse<KnowledgeBaseFileViewDto>> => {
    const formData = new FormData()
    formData.append('file', file)
    const query = fileQuery(assetType, background)
    return apiClient.post(
      `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}/Files${query}`,
      formData
    )
  },

  deleteWorkspaceFile: (
    clientCompanyId: string,
    workspaceId: string,
    category: string,
    fileId: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/KnowledgeBase/${categoryToUrlParam(category)}/Files/${fileId}`),

  // ---------- CAMPAIGN LEVEL ----------

  getCampaignSummary: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string
  ): Promise<ApiResponse<KnowledgeBaseSummaryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase`),

  getCampaignEntry: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  upsertCampaignEntry: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string,
    dto: KnowledgeBaseEntryUpsertDto
  ): Promise<ApiResponse<KnowledgeBaseEntryViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}`, dto),

  deleteCampaignOverride: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}`),

  uploadCampaignFile: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string,
    file: File,
    assetType?: BrandAssetType,
    background?: BrandAssetBackground | null
  ): Promise<ApiResponse<KnowledgeBaseFileViewDto>> => {
    const formData = new FormData()
    formData.append('file', file)
    const query = fileQuery(assetType, background)
    return apiClient.post(
      `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}/Files${query}`,
      formData
    )
  },

  deleteCampaignFile: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    category: string,
    fileId: string
  ): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/KnowledgeBase/${categoryToUrlParam(category)}/Files/${fileId}`),

  // ---------- AI POPULATION (company-wide or a workspace with its own website; async via service bus) ----------
  // Returns 202 Accepted immediately; completion arrives via SignalR "KnowledgeBasePopulated", which names the
  // workspace a workspace run was for. A workspace run always reads that workspace's own website.

  aiPopulate: (
    owner: KnowledgeBaseOwner,
    dto: KnowledgeBaseAiPopulateDto
  ): Promise<ApiResponse<{ status: string; message: string }>> =>
    apiClient.post(`${ownerBase(owner)}/AiPopulate`, dto),

  aiPopulateCategory: (
    owner: KnowledgeBaseOwner,
    category: string,
    dto: KnowledgeBaseAiPopulateDto
  ): Promise<ApiResponse<{ status: string; message: string }>> =>
    apiClient.post(`${ownerBase(owner)}/${categoryToUrlParam(category)}/AiPopulate`, dto),
}
